import type { GameConfig } from './GameState.ts';

/**
 * Difficulty presets, chosen by one criterion above all others: **how often a
 * participant has to be stimulated**.
 *
 * In a mediated run every flap is a real electrical stimulus delivered to a
 * person, so the required flap rate is an ethical budget rather than a taste
 * setting. `measuredFlapsPerSec` below is not an estimate -- it is the rate a
 * direct bot actually needed, measured over 10 seeds x 90 s (`npm run bench`).
 *
 * ## What actually reduces the rate
 *
 * The rate is set by how long the bird can coast between corrections, which is
 * governed by its sink rate (`gravity / drag`) and by how far apart the pipes
 * are. Both of those are visible on screen. There is no invisible setting that
 * makes a bird flap half as often, and it is worth being clear about that:
 *
 *   - **Sink rate** (visible: floatier flight). The big lever. Low gravity on
 *     its own does not work -- the bird just accelerates into a dive more
 *     slowly -- so it has to be paired with `drag`, which gives it a *steady*
 *     sink rate instead of an accelerating one.
 *   - **Pipe spacing** (visible: pipes further apart). The second lever, and it
 *     is not optional at low gravity: a slow-sinking bird physically cannot
 *     descend between two closely spaced gaps. Nothing below ~1.4 flaps/s
 *     survives at the classic 240 px spacing, at any gravity.
 *
 * Three levers *are* invisible, but they buy survival rather than quiet:
 *
 *   - `maxGapCenterStep` -- caps the vertical jump between consecutive gaps.
 *     Every pipe still looks randomly placed.
 *   - `hitboxScale` -- shrinks the collision circle inside the sprite.
 *   - `collisionGraceMs` -- lets a late flap rescue a clip. This is the one
 *     that specifically forgives a *slow participant* rather than a bad aim.
 *
 * ## The cost of a gentle course
 *
 * Difficulty is also the experiment's *sensitivity*, and that is worth choosing
 * deliberately rather than discovering. Measured at 120 ms of uncompensated
 * actuator delay:
 *
 *   Classic   dead in 4 s
 *   Balanced  survives, but asks for 3.2 stimuli/s instead of 1.1
 *   Quiet     survives, but asks for 2.2 stimuli/s instead of 1.0
 *
 * The latency effect does not disappear on a gentle course -- it moves out of
 * the survival time and into the stimulation count. For this experiment that is
 * arguably the better dependent variable: it is measurable without ever letting
 * the participant crash, and it is denominated in the currency that actually
 * matters, which is how many times a person was shocked.
 */
export interface DifficultyPreset {
  id: string;
  label: string;
  description: string;
  /** Measured requirement for a direct bot, flaps per second. */
  measuredFlapsPerSec: number;
  config: Partial<GameConfig>;
}

export const DIFFICULTY_PRESETS: DifficultyPreset[] = [
  {
    id: 'classic',
    label: 'Classic',
    description:
      'Undamped fall, tight spacing, no forgiveness. A stimulus every ~370 ms — too fast for ' +
      'a real participant, but the most sensitive course: uncompensated latency kills it in ' +
      'four seconds. Use it when the effect, not the participant, is the constraint.',
    measuredFlapsPerSec: 2.66,
    config: {
      gravity: 1100, drag: 0, flapImpulse: -380, maxFallSpeed: 600,
      pipeSpeed: 130, pipeGap: 170, pipeSpacing: 240,
      maxGapCenterStep: 0, hitboxScale: 1, collisionGraceMs: 0,
    },
  },
  {
    id: 'balanced',
    label: 'Balanced',
    description:
      'Damped flight at close to the classic layout. A stimulus about every 0.9 s — 2.4× ' +
      'quieter than Classic — while still showing a clear latency effect. The default.',
    measuredFlapsPerSec: 1.11,
    config: {
      gravity: 260, drag: 1.3, flapImpulse: -250, maxFallSpeed: 600,
      pipeSpeed: 130, pipeGap: 170, pipeSpacing: 260,
      maxGapCenterStep: 80, hitboxScale: 0.85, collisionGraceMs: 40,
    },
  },
  {
    id: 'quiet',
    label: 'Quiet',
    description:
      'Floatier flight, wider spacing. About one stimulus per second, and nothing the bot ' +
      'cannot fly. Latency stops causing crashes here and shows up purely as extra stimuli.',
    measuredFlapsPerSec: 0.96,
    config: {
      gravity: 150, drag: 1.2, flapImpulse: -220, maxFallSpeed: 600,
      pipeSpeed: 130, pipeGap: 170, pipeSpacing: 300,
      maxGapCenterStep: 60, hitboxScale: 0.8, collisionGraceMs: 60,
    },
  },
  {
    id: 'minimal',
    label: 'Minimal stimulation',
    description:
      'The quietest course the bot still flies perfectly: a stimulus every ~1.7 s, a 4.5× ' +
      'reduction on Classic. Use it when stimulation budget is the binding constraint.',
    measuredFlapsPerSec: 0.59,
    config: {
      gravity: 120, drag: 1.0, flapImpulse: -200, maxFallSpeed: 600,
      pipeSpeed: 130, pipeGap: 170, pipeSpacing: 300,
      maxGapCenterStep: 50, hitboxScale: 0.75, collisionGraceMs: 90,
    },
  },
];

export function getPreset(id: string): DifficultyPreset | undefined {
  return DIFFICULTY_PRESETS.find((p) => p.id === id);
}

/**
 * Largest vertical step between consecutive gaps the bird can actually cover.
 *
 * The bird has no downward thrust: it descends only at its sink rate, and only
 * during the `pipeSpacing / pipeSpeed` seconds between one gap and the next.
 * Some of that time goes on decelerating out of the previous flap, so the
 * usable fraction is well under one; 0.45 matches where the measured configs
 * stop being flyable.
 *
 * Exposed so the UI can warn when `maxGapCenterStep` has been set past the
 * point where the course is impossible rather than merely hard.
 */
export function maxReachableGapStep(config: GameConfig): number {
  const sink = config.drag > 0
    ? Math.min(config.maxFallSpeed, config.gravity / config.drag)
    : config.maxFallSpeed;
  const secondsBetweenPipes = config.pipeSpacing / Math.max(1, config.pipeSpeed);
  return 0.45 * sink * secondsBetweenPipes;
}

/** The gap step actually in force, resolving 0 to "unlimited". */
export function effectiveGapStep(config: GameConfig): number {
  const span = config.height - 2 * config.gapMargin - config.pipeGap;
  return config.maxGapCenterStep > 0 ? Math.min(config.maxGapCenterStep, span) : span;
}
